import * as React from 'react';
import * as styles from "./Messages.css";
import { inject, observer } from 'mobx-react';
import { MessagesStore } from './stores';
import { Paging } from './paging/Paging';
import { OldViewPanel } from './panels/OldViewPanel';
import { IMessageResult } from '../../interfaces/IMessageResult';
import MessageService from '../../services/MessageService';
import SocialService from '../../services/SocialService';
import { IMessage } from '../../interfaces/IMessage';
import ListView from './listView/ListView';
import { IAButton } from '../button/Button';
import { IAPopup } from '../popup/Popup';
import { IAMessageBar } from '../messageBar/MessageBar';
import { Helper } from '../../Helper';
import { LocalizationService } from "../../services/LocalizationService";
import Rotator from './rotator/Rotator';
import { IAPivot } from '../pivot/Pivot';
import { IACategories } from '../categories/Categories';
import { ICategoryAdmin } from '../../interfaces/ICategoryAdmin';
import TilesView from './tilesView/TilesView';
import CarrouselView from './carouselView/CarouselView';
import { FilterService } from './Filter.service';
import { Header } from './header/Header';
import EventService from '../../services/EventService';
import EventListView from './listView/EventListView';
import { FullView } from './fullView/FullView';
import AnalyticsService from '../../services/AnalyticsService';
import DateTimeService from '../../services/DateTimeService';
import { IMessageAnalytics } from '../../interfaces/IMessageAnalytics';
import "../../styles/BasicIA.css";
import { CompactListView } from '../messageCompactListView/MessageCompactListView';
import { EditPanel } from './panels/EditPanel';
import { ViewPanel } from './panels/ViewPanel';
import CardView from './cardView/CardView';
import { ReelsView } from './reelsView/ReelsView';
import { ReelsPopup } from './reelsView/ReelsPopup';
import { IHttpStatus } from '../../interfaces/IHttpStatus';
import { LoggingService } from '../../services/LoggingService';
import Utils from '../treeTargeting/utils';
import { IAIcon } from '../icon/Icon';
import { SortPinnedPanel } from './panels/SortPinnedPanel';
import { DigestPanel } from './panels/DigestPanel';
import MessageTemplateService from '../../services/MessageTemplateService';
import EventTemplateService from '../../services/EventTemplateService';
import { IFilterDataMessages } from '../../interfaces/IFilterDataMessages';
import CalendarView from './calendarView/CalendarView';
import { IARecurringEvents } from './recurringEvents/RecurringEvents';
import { ILike } from '../../interfaces/ILike';
import { IEditableTitleChanges } from '../editableTitle/EditableTitle';

export interface IMessagesProps {
  instanceId: number;
  messagesStore?: MessagesStore;
  close?: () => void;
  panelIsOpen?: (panelIsOpen: boolean) => void;
  noContentCallback?: () => void; // only used in app
  hasOpenedContentFromLinkCallback?: () => void; // used to open message from link
  targetingIsEnabled?: (enabled: boolean) => void // only used in admin
  saveTemplateCallback?: (jsonTemplate: string) => void;
  onTitleSettingsChange?: (changes: IEditableTitleChanges) => void;
}

export interface IMessagesState {
  selectedTab: "Messages" | "Recurring" | "Categories";
  shouldReloadOnFocus: boolean;
}

@inject('messagesStore')
@observer
export class Messages extends React.Component<IMessagesProps, IMessagesState> {
  private readonly constructorName = "Messages";

  private notificationBarId;
  private deletePopupId;
  private cancelPopupId;
  private convertPopupId;
  private messagesResultCount: number;
  private pinnedMessagesResultCount: number;
  private _isMounted: boolean;
  private readonly localizationService: LocalizationService = new LocalizationService();

  constructor(props: IMessagesProps) {
    super(props);
    this.state = {
      selectedTab: "Messages",
      shouldReloadOnFocus: true
    }
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());
    this.messagesResultCount = undefined;
    this.pinnedMessagesResultCount = undefined;
    this.props.messagesStore!.loading = true;
    LoggingService.init(this.props.messagesStore!.environment);
    if (this.props.messagesStore!.language != undefined) {
      this.props.messagesStore!.setLanguage(this.props.messagesStore!.language);
    }
    if (localStorage.getItem('targetedPreviewIds') && this.props.messagesStore) {
      this.props.messagesStore.targetedPreviewIds = JSON.parse(localStorage.getItem('targetedPreviewIds') ?? "[]");
    }

    if (location.search.indexOf("inteams") !== -1) {
      this.props.messagesStore!.inTeams = true;
    }
    this.notificationBarId = Helper.getRandomStringKey();
    this.deletePopupId = Helper.getRandomStringKey();
    this.cancelPopupId = Helper.getRandomStringKey();
  }

  public async componentDidMount(): Promise<void> {
    this._isMounted = true;
    if (this.props.messagesStore!.isMobile) {
      const viewport = document.querySelector("meta[name=viewport]");
      viewport.setAttribute('content', 'viewport-fit=cover, width=device-width, initial-scale=1.0, maximum-scale=5.0');
    }
    if (this.props.messagesStore!.isNavigator && this.props.messagesStore!.openContentFromLink) {
      this.props.messagesStore!.applicationSettings = this.props.messagesStore!.openContentFromLink?.targetingApplications;
      this.props.messagesStore!.targetingProperties = this.props.messagesStore!.openContentFromLink?.targetingProperties;
      this.props.messagesStore!.setUserRole(this.props.messagesStore!.openContentFromLink?.userRole);
      this.isCommentsAndLikesEnabled().then(() => {
        if (this.props.messagesStore!.openContentFromLink.component === "Messages") {
          this.getMessageFromInstance(this.props.messagesStore!.instance.instanceName, this.props.messagesStore!.openContentFromLink.idToOpen);
        } else {
          this.getEventFromInstance(this.props.messagesStore!.instance.instanceName, this.props.messagesStore!.openContentFromLink.idToOpen);
        }
      });
    }

    if (this.props.messagesStore!.isMessagesTemplateEditor) {
      await this.props.messagesStore!.getUserRole();
      await this.isCommentsAndLikesEnabled();
    }

    const urlParams = new URL(window.location.toString()).searchParams;
    if (urlParams.get("categories")) {
      const ids = urlParams.get("categories").split(",");
      const filter: IFilterDataMessages = {
        isFiltered: true,
        selectedCategories: ids.map(id => { return { id } }),
        selectedPeolple: [],
        showOnlyAuthorMessages: undefined,
        showPeriodMessage: undefined
      };
      this.props.messagesStore!.updateFilter(filter);
    }

    // Resize
    window.addEventListener('resize', () => this.resize());
    this.resize();
    setTimeout(() => {
      this.resize();
    }, 0);
    try {
      var resizeObserver = new ResizeObserver(() => {
        this.resize();
      });
      resizeObserver.observe(document.getElementById(`${this.props.messagesStore!.id}`));
    } catch {
      this.resize();
    }
    this.onInstanceChange();
  }

  public componentWillReceiveProps(props: IMessagesProps): void {
    if (this.props.messagesStore!.shouldReQuery) {
      this.props.messagesStore!.reload();
    }
    if (this.props.messagesStore!.reloadComponent && !this.props.messagesStore!.loading) {
      localStorage.removeItem(`intraactive-${this.props.messagesStore!.component === "Event" ? "events" : "messages-v2"}-${this.props.messagesStore!.webpartId}`);
      this.props.messagesStore!.reloadComponent = false;
      this.props.messagesStore!.messages = [];
      this.props.messagesStore!.loading = true;
      this.getMessages();
    }
    if (this.props.messagesStore!.language != undefined) {
      this.props.messagesStore!.setLanguage(props.messagesStore.language);
    }
    if (props.instanceId !== this.props.instanceId) {
      this.onInstanceChange();
    }
  }

  private onInstanceChange(): void {
    if (!this.props.messagesStore!.isNavigator && !this.props.messagesStore!.isMessagesTemplateEditor) {
      this.getMessagesFromCache();
      if (!this.props.messagesStore!.isAdminPage && this.props.messagesStore!.onlyShowIfThereIsContent && (!this.props.messagesStore!.userRole || (this.props.messagesStore!.userRole === "Administrator" && this.props.messagesStore!.webpartDisplayMode === 1) || (this.props.messagesStore!.userRole === "Editor" && this.props.messagesStore!.webpartDisplayMode === 1 || (this.props.messagesStore!.userRole === "Reader"))) && !(this.props.messagesStore!.messages?.length > 0) && !this.props.messagesStore!.isSearching && this.props.messagesStore!.searchText == "" && !this.props.messagesStore!.filterData.isFiltered) {
        this.props.messagesStore!.toggleShowHideComponent(true);
      }
      // Targeting
      this.props.messagesStore!.getTargetingApplication().then(() => {
        if (this.props.targetingIsEnabled) {
          const targetingEnabled = Utils.checkIfUsesTargeting(this.props.messagesStore?.applicationSettings, this.props.messagesStore?.instance?.instanceName);
          this.props.targetingIsEnabled(targetingEnabled);
        }
        this.props.messagesStore!.getTargetingProperties().then(() => {
          this.getMessages();
        });
      });
    }
  }

  //////////////////////////////////////////// Resize ////////////////////////////////////////////

  private resize(): void {
    this.props.messagesStore!.width = document.getElementById(`${this.props.messagesStore!.id}`) != undefined ? document.getElementById(`${this.props.messagesStore!.id}`).offsetWidth : 0;
    if (this.props.messagesStore!.isAdminPage) {
      this.props.messagesStore!.width = Math.floor(this.props.messagesStore!.width - 42);
    } else {
      this.props.messagesStore!.width = Math.floor(this.props.messagesStore!.width - 3);
    }
  }

  ////////////////////////////////////////////// Fetch //////////////////////////////////////////////

  private getMessagesFromCache(): void {
    const component = this.props.messagesStore!.component;
    const localStorageMessages = localStorage.getItem(`intraactive-${component === "Event" ? "events" : "messages-v2"}-${this.props.messagesStore!.webpartId}`);
    if (localStorageMessages && !this.props.messagesStore!.isAdminPage && !this.props.messagesStore!.isNavigator) {
      this.props.messagesStore!.cacheTimestamp = JSON.parse(localStorageMessages).timestamp;
      this.props.messagesStore!.messages = JSON.parse(localStorageMessages).messages;
      if (this.props.messagesStore!.messages?.length > 0) {
        this.props.messagesStore!.messages = this.props.messagesStore!.messages.filter((message: IMessage) => {
          if (this.props.messagesStore!.display === "Calendar") {
            return message.endDate == undefined; // also show events in the past when in calendar view
          }
          return message.endDate == undefined || new Date(message.endDate) > new Date();
        });
      }

      // also check if likes and comments is enabled so we can show the icons
      const instanceName = this.props.messagesStore!.instance.instanceName;
      const localStorageCommentsAndLikesEnabled = localStorage.getItem(component === "Messages" ? `IA_areCommentsAndLikesEnabledMessages_${instanceName}` : `IA_areCommentsAndLikesEnabledEvents_${instanceName}`);
      if (localStorageCommentsAndLikesEnabled) {
        this.props.messagesStore!.likesEnabled = JSON.parse(localStorageCommentsAndLikesEnabled).value?.likesEnabled;
        this.props.messagesStore!.commentsEnabled = JSON.parse(localStorageCommentsAndLikesEnabled).value?.commentsEnabled;
        if (this.props.messagesStore!.likesEnabled || this.props.messagesStore!.commentsEnabled) {
          this.props.messagesStore!.loadingCommentsAndLikes = true;
        }
      }

      if (this.props.messagesStore!.showUniqueViews) {
        this.props.messagesStore!.loadingUniqueViews = true;
      }

      this.props.messagesStore!.showingContentFromCache = true;
      this.props.messagesStore!.loading = false;
      if (this.props.messagesStore!.display === "Calendar" && !(this.props.messagesStore!.messages?.length > 0)) {
        this.props.messagesStore!.loading = true;
      }
    } else {
      this.props.messagesStore!.loading = true;
      this.props.messagesStore!.messages = [];
    }
  }

  private async getMessages(): Promise<void> {
    if (!this._isMounted) {
      return;
    }

    const env = this.props.messagesStore!.environment ?? "Production";
    this.props.messagesStore!.pagingInfo = undefined;
    this.props.messagesStore!.isFetchingMessages = true;
    await this.props.messagesStore!.getInstance();
    if (this.props.messagesStore!.component === "Event") {
      EventService.getEvents(env, this.props.messagesStore!.token, this.props.messagesStore!.tenant, this.props.messagesStore!.instance.instanceName, this.props.messagesStore!.isAdminPage || this.props.messagesStore!.targetedPreviewIds.length > 0 ? false : this.props.messagesStore!.isTargetingAvailable, FilterService.get(this.props.messagesStore!.isAdminPage && this.props.messagesStore!.display !== "Calendar", this.props.messagesStore!.filterData, this.props.messagesStore!.shouldFilterByCategory, this.props.messagesStore!.selectedCategories, this.props.messagesStore!.targetedPreviewIds?.length > 0 ? 50 : this.props.messagesStore!.numMessages, this.props.messagesStore!.pagingInfo, this.props.messagesStore!.component, this.props.messagesStore!.startDate, this.props.messagesStore!.endDate, this.props.messagesStore!.showPeriod), this.props.messagesStore!.filterData.isFiltered, this.props.messagesStore!.showPeriod).then((messagesResult: IMessageResult) => {
        this.handleMessagesResult(messagesResult);
      });
    } else {

      const hasTargeting = this.props.messagesStore!.isAdminPage || this.props.messagesStore!.targetedPreviewIds.length > 0 ? false : this.props.messagesStore!.isTargetingAvailable;

      // fetch pinned messages if it is turned on
      // else fetch normal (unpinned) messages
      if (this.props.messagesStore!.instance?.settings?.enablePinMessages && this.props.messagesStore!.showPinnedMessages && !this.props.messagesStore!.isAdminPage) {
        MessageService.getActivePinnedMessages(env, this.props.messagesStore!.token, this.props.messagesStore!.tenant, this.props.messagesStore!.instance.instanceName, hasTargeting, FilterService.get(this.props.messagesStore!.isAdminPage, this.props.messagesStore!.filterData, this.props.messagesStore!.shouldFilterByCategory, this.props.messagesStore!.selectedCategories, this.props.messagesStore!.targetedPreviewIds?.length > 0 ? 50 : this.props.messagesStore!.numMessages, this.props.messagesStore!.pagingInfo, this.props.messagesStore!.component), this.props.messagesStore!.filterData.isFiltered, this.props.messagesStore!.searchText).then((pinnedMessagesResult: IMessageResult) => {
          // use the custom sort order if it is set
          if (this.props.messagesStore!.instance?.settings?.pinSortOrder && pinnedMessagesResult.messages?.length > 0) {
            pinnedMessagesResult.messages = this.props.messagesStore!.sortMessageBySortOrder(pinnedMessagesResult.messages, this.props.messagesStore!.instance.settings.pinSortOrder);
          }

          if (pinnedMessagesResult.messages?.length === 0) {
            this.props.messagesStore!.hideSortPanelButton = true;
          }

          if (this.props.messagesStore!.onlyShowPinnedMessages) {
            this.handlePinnedMessages(pinnedMessagesResult);
            return;
          }

          const pinnedAmount = pinnedMessagesResult.messages.length;
          const messagesPerPage = this.props.messagesStore!.targetedPreviewIds?.length > 0 ? 50 : this.props.messagesStore!.numMessages;

          const quotient = Math.floor(pinnedAmount / messagesPerPage);
          const numMessagesRemainder = ((quotient + 1) * messagesPerPage) - pinnedAmount;

          // fetch normal (unpinned) messages to fill out the remainder of the last page of pinned messages
          MessageService.getMessages(env, this.props.messagesStore!.token, this.props.messagesStore!.tenant, this.props.messagesStore!.instance.instanceName, hasTargeting, FilterService.get(this.props.messagesStore!.isAdminPage, this.props.messagesStore!.filterData, this.props.messagesStore!.shouldFilterByCategory, this.props.messagesStore!.selectedCategories, numMessagesRemainder, this.props.messagesStore!.pagingInfo, this.props.messagesStore!.component), this.props.messagesStore!.filterData.isFiltered, this.props.messagesStore!.searchText).then((messagesResult: IMessageResult) => {
            const pinnedMessages = pinnedMessagesResult?.messages ?? [];
            const normalMessages = messagesResult?.messages ?? [];
            messagesResult.messages = [...pinnedMessages, ...normalMessages];

            const text = this.props.messagesStore!.searchText;
            if (!!text && !!messagesResult.searchValue) {
              if (text === messagesResult.searchValue) {
                this.handleMessagesResult(messagesResult);
              }
            } else {
              this.handleMessagesResult(messagesResult);
            }
          });
        });
      } else {
        MessageService.getMessages(env, this.props.messagesStore!.token, this.props.messagesStore!.tenant, this.props.messagesStore!.instance.instanceName, hasTargeting, FilterService.get(this.props.messagesStore!.isAdminPage, this.props.messagesStore!.filterData, this.props.messagesStore!.shouldFilterByCategory, this.props.messagesStore!.selectedCategories, this.props.messagesStore!.targetedPreviewIds?.length > 0 ? 50 : this.props.messagesStore!.numMessages, this.props.messagesStore!.pagingInfo, this.props.messagesStore!.component), this.props.messagesStore!.filterData.isFiltered, this.props.messagesStore!.searchText, !this.props.messagesStore!.isAdminPage).then((messagesResult: IMessageResult) => {
          const text = this.props.messagesStore!.searchText;
          if (!!text && !!messagesResult.searchValue) {
            if (text === messagesResult.searchValue) {
              this.handleMessagesResult(messagesResult);
            }
          } else {
            this.handleMessagesResult(messagesResult);
          }
        });
      }
    }
  }

  // only show messages without targeting, or targeted to one of the previewed groups
  private filterMessagesForTargetedPreview(messages: IMessage[]): IMessage[] {
    const targetedPreviewIds = this.props.messagesStore!.targetedPreviewIds;
    if (!targetedPreviewIds || targetedPreviewIds.length === 0 || !messages) {
      return messages;
    }
    return messages.filter((message: IMessage) => {
      if (!message.iaTargetingTargetContent) {
        return true;
      }
      return targetedPreviewIds.some((id: number) => message.iaTargetingGroupData?.includes(`;${id};`));
    });
  }

  private async handleMessagesResult(messagesResult: IMessageResult): Promise<void> {
    this.props.messagesStore!.lastRequestForNewContent = new Date();
    //this.shouldCheckForNewContent();
    this.props.messagesStore!.loading = false;
    if (messagesResult.httpStatus.statusCode == 200) {
      messagesResult.messages = this.filterMessagesForTargetedPreview(messagesResult.messages);
      this.messagesResultCount = messagesResult.messages.length;
      if (!this.props.messagesStore!.isAdminPage && this.props.messagesStore!.onlyShowIfThereIsContent && (!this.props.messagesStore!.userRole || (this.props.messagesStore!.userRole === "Administrator" && this.props.messagesStore!.webpartDisplayMode === 1) || (this.props.messagesStore!.userRole === "Editor" && this.props.messagesStore!.webpartDisplayMode === 1 || (this.props.messagesStore!.userRole === "Reader"))) && this.messagesResultCount === 0 && !this.props.messagesStore!.isSearching && this.props.messagesStore!.searchText == "" && !this.props.messagesStore!.filterData.isFiltered) {
        if (this.props.noContentCallback !== undefined && this.props.messagesStore!.isMobileApp) {
          this.props.noContentCallback();
        }
        this.props.messagesStore!.toggleShowHideComponent(true);
      } else {
        this.props.messagesStore!.toggleShowHideComponent(false);
      }
      if (this.props.messagesStore!.messages && messagesResult.messages && JSON.stringify(this.props.messagesStore!.messages) != JSON.stringify([messagesResult.messages])) {
        this.props.messagesStore!.messages = messagesResult.messages;
      }
      this.props.messagesStore!.cacheTimestamp = new Date();
      this.props.messagesStore!.showingContentFromCache = false;
      this.props.messagesStore!.pagingInfo = messagesResult.pagingInfo;
      this.saveCache();
      this.resize();
    } else {
      console.log("ERROR: getMessages failed!");
      console.log(messagesResult.httpStatus);
    }
    this.finishHandleMessages();
  }

  private handlePinnedMessages(messagesResult: IMessageResult) {
    if (messagesResult.httpStatus.statusCode == 200) {
      messagesResult.messages = this.filterMessagesForTargetedPreview(messagesResult.messages);
      this.props.messagesStore!.messages = messagesResult.messages;
      this.props.messagesStore!.cacheTimestamp = new Date();
      this.pinnedMessagesResultCount = messagesResult.messages?.length;
      this.saveCache();
      this.props.messagesStore!.loading = false;
      if (!this.props.messagesStore!.isAdminPage && this.props.messagesStore!.onlyShowIfThereIsContent && (!this.props.messagesStore!.userRole || (this.props.messagesStore!.userRole === "Administrator" && this.props.messagesStore!.webpartDisplayMode === 1) || (this.props.messagesStore!.userRole === "Editor" && this.props.messagesStore!.webpartDisplayMode === 1 || (this.props.messagesStore!.userRole === "Reader"))) && this.pinnedMessagesResultCount === 0 && !this.props.messagesStore!.isSearching && this.props.messagesStore!.searchText == "" && !this.props.messagesStore!.filterData.isFiltered) {
        if (this.props.noContentCallback !== undefined && this.props.messagesStore!.isMobileApp) {
          this.props.noContentCallback();
        }
        this.props.messagesStore!.toggleShowHideComponent(true);
      } else {
        this.props.messagesStore!.toggleShowHideComponent(false);
      }
    }
    this.finishHandleMessages();
  }

  private async finishHandleMessages() {
    if (this.props.messagesStore!.addMessage) {
      await this.getTemplatesForInstance();
    }
    this.props.messagesStore!.isFetchingMessages = false;
    if (this.props.messagesStore!.messages?.length > 0) {
      let itemIds = [];
      if (this.messagesResultCount != 0) {
        this.props.messagesStore!.messages.forEach((message: IMessage) => {
          itemIds.push(message.id);
        });
      }
      // Unique views
      if (this.props.messagesStore!.component === "Messages") {
        this.getUniqueViews(itemIds);
      }
      // User role
      await this.props.messagesStore!.getUserRole();
      // Comments and likes
      this.isCommentsAndLikesEnabled().then(() => {
        this.getCommentsAndLikes(itemIds);
      });
    } else {
      await this.props.messagesStore!.getUserRole();
    }
  }

  private saveCache() {
    if (this.props.messagesStore!.webpartId && !this.props.messagesStore!.isNavigator && this.props.messagesStore!.messages.length > 0) {
      Helper.setLocalStorage(`intraactive-${this.props.messagesStore!.component === "Event" ? "events" : "messages-v2"}-${this.props.messagesStore!.webpartId}`, JSON.stringify({
        "messages": this.props.messagesStore!.messages,
        "timestamp": new Date()
      }));
    }
  }

  ////////////////////////////////////////// Fetch templates //////////////////////////////////////////

  private getTemplatesForInstance(): Promise<void> {
    return new Promise((resolve) => {
      if (this.props.messagesStore!.component === "Messages") {
        MessageTemplateService.getTemplatesForUser(this.props.messagesStore!.environment, this.props.messagesStore!.tenant, this.props.messagesStore!.instance?.instanceName)
          .then(response => this.props.messagesStore!.availableTemplates = response?.templates)
          .finally(() => resolve());
      }
      if (this.props.messagesStore!.component === "Event") {
        EventTemplateService.getTemplatesForUser(this.props.messagesStore!.environment, this.props.messagesStore!.tenant, this.props.messagesStore!.instance?.instanceName)
          .then(response => this.props.messagesStore!.availableTemplates = response?.templates)
          .finally(() => resolve());
      }
    });
  }

  ////////////////////////////////////////// Fetch one message //////////////////////////////////////////

  private getMessageFromInstance(instanceName: string, messageId: number) {
    MessageService.getMessage(
      this.props.messagesStore!.environment != undefined ? this.props.messagesStore!.environment : "Production",
      this.props.messagesStore!.token,
      this.props.messagesStore!.tenant,
      instanceName,
      messageId
    ).then((messagesResult: IMessageResult) => {
      this.handleMessageResult(messagesResult);
    }).catch(() => {
      if (this.props.hasOpenedContentFromLinkCallback) this.props.hasOpenedContentFromLinkCallback();
    });
  }

  private getEventFromInstance(instanceName: string, eventId: number) {
    EventService.getEvent(
      this.props.messagesStore!.environment != undefined ? this.props.messagesStore!.environment : "Production",
      this.props.messagesStore!.token,
      this.props.messagesStore!.tenant,
      instanceName,
      eventId
    ).then((messagesResult: IMessageResult) => {
      this.handleMessageResult(messagesResult);
    }).catch(() => {
      if (this.props.hasOpenedContentFromLinkCallback) this.props.hasOpenedContentFromLinkCallback();
    });
  }

  private handleMessageResult(messagesResult: IMessageResult): void {
    let itemIds = [];
    if (messagesResult.messages.length != 0) {
      messagesResult.messages.forEach((message: IMessage) => {
        itemIds.push(message.id);
      });
      var message = messagesResult.messages[0];
      message.likes = undefined;
      message.commentCount = undefined;
      this.props.messagesStore!.messages = [message];
      if (this.props.messagesStore!.instance?.settings?.type === "reels") {
        this.props.messagesStore!.reelsMessageToOpen = message;
        this.props.messagesStore!.loading = false;
        this.props.messagesStore!.getUserRole().then(() => {
          this.isCommentsAndLikesEnabled().then(() => {
            this.getCommentsAndLikes(itemIds);
          });
        });
        if (this.props.hasOpenedContentFromLinkCallback) this.props.hasOpenedContentFromLinkCallback();
      } else {
        this.props.messagesStore!.openMessage(message, this.props.hasOpenedContentFromLinkCallback);
      }
    } else {
      this.props.messagesStore!.showNotification(
        "error",
        this.localizationService.strings.Messages_ErrorPopup_Deleted,
        "Close"
      );
      if (this.props.hasOpenedContentFromLinkCallback) this.props.hasOpenedContentFromLinkCallback();
    }
  }

  //////////////////////////////////////////// Paging ////////////////////////////////////////////

  private getPreviousPage(): void {
    this.props.messagesStore!.pageNumber--;
    LoggingService.trackEvent(this.constructorName, {
      ActionType: "PagingPreviousPage",
      Component: this.props.messagesStore!.component,
      Tenant: this.props.messagesStore!.tenant,
      Instance: this.props.messagesStore!.instance.instanceName,
      UserId: this.props.messagesStore!.token,
      Environment: this.props.messagesStore!.environment
    });
  }

  private hasNextPage(): boolean {
    if (this.props.messagesStore!.targetedPreviewIds?.length > 0) {
      return false;
    }
    return !this.props.messagesStore!.loading && (this.props.messagesStore!.pagingInfo !== "" || Math.ceil(this.props.messagesStore!.messages.length / this.props.messagesStore!.numMessages) > this.props.messagesStore!.pageNumber);
  }

  private showPaging(): boolean {
    if (this.props.messagesStore!.targetedPreviewIds?.length > 0) {
      return false;
    }
    if (this.props.messagesStore!.messages?.length > 0 && this.props.messagesStore!.pagingInfo) {
      return true;
    } else if (this.props.messagesStore!.messages?.length > this.props.messagesStore!.numMessages) {
      return true;
    } else {
      return false;
    }
  }

  private async getNextPage(): Promise<void> {
    this.props.messagesStore!.loading = true;
    if (this.props.messagesStore!.component === "Event") {
      await EventService.getEvents(this.props.messagesStore!.environment != undefined ? this.props.messagesStore!.environment : "Production", this.props.messagesStore!.token, this.props.messagesStore!.tenant, this.props.messagesStore!.instance.instanceName, this.props.messagesStore!.isAdminPage ? false : this.props.messagesStore!.isTargetingAvailable, FilterService.get(this.props.messagesStore!.isAdminPage, this.props.messagesStore!.filterData, this.props.messagesStore!.shouldFilterByCategory, this.props.messagesStore!.selectedCategories, this.props.messagesStore!.numMessages, this.props.messagesStore!.pagingInfo, this.props.messagesStore!.component, this.props.messagesStore!.startDate, this.props.messagesStore!.endDate, this.props.messagesStore!.showPeriod), this.props.messagesStore!.filterData.isFiltered, this.props.messagesStore!.showPeriod).then((messagesResult: IMessageResult) => {
        this.handlePagingMessagesResult(messagesResult);
        if (this.props.messagesStore!.messageToOpen) {
          this.props.messagesStore!.openNextMessage();
        }
      });
    } else {
      await MessageService.getMessages(this.props.messagesStore!.environment != undefined ? this.props.messagesStore!.environment : "Production", this.props.messagesStore!.token, this.props.messagesStore!.tenant, this.props.messagesStore!.instance.instanceName, this.props.messagesStore!.isAdminPage ? false : this.props.messagesStore!.isTargetingAvailable, FilterService.get(this.props.messagesStore!.isAdminPage, this.props.messagesStore!.filterData, this.props.messagesStore!.shouldFilterByCategory, this.props.messagesStore!.selectedCategories, this.props.messagesStore!.numMessages, this.props.messagesStore!.pagingInfo, this.props.messagesStore!.component), this.props.messagesStore!.filterData.isFiltered, this.props.messagesStore!.searchText).then((messagesResult: IMessageResult) => {
        this.handlePagingMessagesResult(messagesResult);
        if (this.props.messagesStore!.messageToOpen) {
          this.props.messagesStore!.openNextMessage();
        }
      });
    }
    LoggingService.trackEvent(this.constructorName, {
      ActionType: "PagingNextPage",
      Component: this.props.messagesStore!.component,
      Tenant: this.props.messagesStore!.tenant,
      Instance: this.props.messagesStore!.instance.instanceName,
      UserId: this.props.messagesStore!.token,
      Environment: this.props.messagesStore!.environment
    });
  }

  private handlePagingMessagesResult(messagesResult: IMessageResult): void {
    if (messagesResult.httpStatus.statusCode == 200) {
      messagesResult.messages = this.filterMessagesForTargetedPreview(messagesResult.messages);
      this.props.messagesStore!.messages = this.props.messagesStore!.messages.concat(messagesResult.messages);
      this.props.messagesStore!.pagingInfo = messagesResult.pagingInfo;
      this.props.messagesStore!.loading = false;
      let itemIds = [];
      messagesResult.messages.forEach((message: IMessage) => {
        itemIds.push(message.id);
      });
      if (this.props.messagesStore!.component === "Messages" && this.props.messagesStore!.showUniqueViews) {
        this.getUniqueViews(itemIds);
      }
      this.getCommentsAndLikes(itemIds);
    } else {
      console.log("ERROR: getMessages failed!");
      console.log(messagesResult.httpStatus);
    }
  }

  //////////////////////////////////////////// Likes and Comments ////////////////////////////////////////////

  private isCommentsAndLikesEnabled(): Promise<void> {
    return new Promise((resolve) => {
      let component;
      if (this.props.messagesStore!.component === "Event") {
        component = "Events"; // SocialService expects event-components to be called "Events" and not "Event"
      } else {
        component = this.props.messagesStore!.component;
      }
      if (component === "Messages" || component === "Events") {
        SocialService.areCommentsAndLikesEnabled(this.props.messagesStore!.environment, this.props.messagesStore!.tenant, this.props.messagesStore!.token, this.props.messagesStore!.instance.instanceName, component, this.props.messagesStore!.isAdminPage).then((response: { httpStatus: IHttpStatus, likesEnabled?: boolean, commentsEnabled?: boolean }) => {
          console.log("areCommentsAndLikesEnabled finished call");
          if (response.httpStatus.statusCode === 200) {
            this.props.messagesStore!.likesEnabled = response.likesEnabled;
            this.props.messagesStore!.commentsEnabled = response.commentsEnabled;
          } else {
            console.log("ERROR: areCommentsAndLikesEnabled failed!");
            console.log(response.httpStatus);
          }
          resolve();
        });
      } else {
        resolve();
      }
    });
  }

  private getCommentsAndLikes(itemIds: any[]): void {
    if (this.props.messagesStore!.likesEnabled || this.props.messagesStore!.commentsEnabled) {
      if (this.props.messagesStore!.user != undefined) {
        this.props.messagesStore!.loadingCommentsAndLikes = true;
        SocialService.getMultipleLikesAndCommentCounts(this.props.messagesStore!.environment, this.props.messagesStore!.tenant, this.props.messagesStore!.component === "Messages" ? "Message" : "Event", this.props.messagesStore!.instance.instanceName, this.props.messagesStore!.token, itemIds).then((result: { httpStatus: IHttpStatus, commentCounts?: number[], likes?: any[] }) => {
          if (result.httpStatus.statusCode === 200) {
            if (this.props.messagesStore!.messages) {
              this.props.messagesStore!.messages.forEach((message: IMessage) => {
                if (result.likes[message.id] != undefined) {
                  message.likes = result.likes[message.id];
                };
                if (result.commentCounts[message.id] != undefined) {
                  message.commentCount = result.commentCounts[message.id];
                }
              });
              this.props.messagesStore!.loadingCommentsAndLikes = false;
              this.props.messagesStore!.showingContentFromCache = false;
              if (this.props.messagesStore!.reelsMessageToOpen) {
                this.props.messagesStore!.reelsMessageToOpen = { ...this.props.messagesStore!.reelsMessageToOpen };
              }
            }
          } else {
            this.props.messagesStore!.showComments = false;
            this.props.messagesStore!.showLikes = false;
            console.log("ERROR: getCommentsAndLikes failed!");
            console.log(result.httpStatus);
          }
          this.resize();
        });
      } else {
        if (!this.props.messagesStore!.loading) {
          setTimeout(() => {
            this.getCommentsAndLikes(itemIds);
          }, 1000);
        }
      }
    }
  }

  //////////////////////////////////////////// Unique Views ///////////////////////////////////////////

  private getUniqueViews(itemIds: number[]): void {
    if (this.props.messagesStore!.component != "Messages") {
      return;
    }
    this.props.messagesStore!.loadingUniqueViews = true;
    setTimeout(() => {
      var dateTimeService: DateTimeService = new DateTimeService();
      // if the page has no content
      if (itemIds.length === 0) {
        return;
      }
      AnalyticsService.getBySourceRollup(this.props.messagesStore!.environment, this.props.messagesStore!.token, this.props.messagesStore!.tenant, this.props.messagesStore!.instance.instanceName, dateTimeService.ConvertToDDMMYYYYWithSlashes(new Date("2020-01-01")), dateTimeService.ConvertToDDMMYYYYWithSlashes(new Date()), itemIds, "total").then(async (result) => {
        if (result.httpStatus.statusCode === 200) {
          var resultDict: { [k: number]: number } = {}
          if (result?.value?.length > 0) {
            result.value.forEach((item: IMessageAnalytics) => {
              resultDict[item.messageId] = item.uniqueViews;
            });
          }
          for (var message of this.props.messagesStore!.messages) {
            if (!message.uniqueViews) {
              const analyticsViews = resultDict[message.id] ?? 0;
              let twelve_hours_ago: number = new Date().getTime() - (60 * 60 * 1000 * 12);
              var createdTime: number;
              try {
                createdTime = message.created.getTime();
              } catch (error) {
                createdTime = 0;
              }
              // Determines if we should fetch data directly from AppInsights instead of Analytics based on:
              // if created date is less than twelve hours ago 
              // AND
              // if there is less than 10 unique views from analytics
              // if there is more likes then there is unique views from analytics
              // if there is more comments then there is unique views from analytics
              if (createdTime > twelve_hours_ago && (analyticsViews < 10 || message.likes?.length > analyticsViews || message.commentCount > analyticsViews)) {
                try {
                  const response = await AnalyticsService.getUniqueViewsDirectlyFromAppInsights(this.props.messagesStore!.environment, this.props.messagesStore!.token, this.props.messagesStore!.tenant, this.props.messagesStore!.instance.instanceName, message.id);
                  // makes sure that the newly fetched views is not lower than the views from analytics
                  if (analyticsViews < response.uniqueViews) {
                    message.uniqueViews = response.uniqueViews;
                  } else {
                    message.uniqueViews = analyticsViews;
                  }
                } catch (error) {
                  console.error("Failed to get unique views directly from AppInsights", error);
                  message.uniqueViews = analyticsViews;
                }
              } else {
                message.uniqueViews = analyticsViews;
              }
            }
          }
        } else {
          this.props.messagesStore!.showUniqueViews = false;
          console.log("ERROR: getMessages failed!");
          console.log(result.httpStatus);
        }
        this.props.messagesStore!.loadingUniqueViews = false;
        this.resize();
      });
    }, 500);
  }

  //////////////////////////////////////////// Render ////////////////////////////////////////////

  public render(): JSX.Element {
    try {
      if (this.props.messagesStore!.messageToOpen != undefined || this.props.messagesStore!.messageToEdit != undefined || this.props.messagesStore!.messageToDelete != undefined || this.props.messagesStore!.showFilterPanel != undefined) {
        this.props.panelIsOpen(true);
      } else {
        this.props.panelIsOpen(false);
      }
    } catch {
    }

    const hasNoContentAndIsNotFiltering = this.props.messagesStore!.searchText === "" && !this.props.messagesStore!.filterData.isFiltered && (this.messagesResultCount === undefined || this.messagesResultCount === 0);
    const isPinnedEnabled = this.props.messagesStore!.instance?.settings?.enablePinMessages && this.props.messagesStore!.showPinnedMessages && !this.props.messagesStore!.isAdminPage;
    const hasPinnedMessages = isPinnedEnabled && this.pinnedMessagesResultCount && this.pinnedMessagesResultCount > 0;
    const isNotAdminOrEditing = !this.props.messagesStore!.isAdminPage && this.props.messagesStore!.webpartDisplayMode === 1;
    const hideComponent = hasNoContentAndIsNotFiltering && isNotAdminOrEditing && !hasPinnedMessages && this.props.messagesStore!.onlyShowIfThereIsContent;
    const showNoContentMessage = hasNoContentAndIsNotFiltering && !this.props.messagesStore!.onlyShowIfThereIsContent && !this.props.messagesStore!.loading && !this.props.messagesStore!.isFetchingMessages && (!this.props.messagesStore!.messages || this.props.messagesStore!.messages?.length === 0);

    let openInNewViewer = this.props.messagesStore!.instance && this.props.messagesStore!.messageToOpen && this.props.messagesStore!.messageToOpen.iaConfiguration && this.props.messagesStore!.messageToOpen.iaConfiguration.composerComponents;
    let newContentLabelWidth;
    if (this.props.messagesStore!.newContentAvailable) {
      if (document.getElementById(`IA_NewContentWrapper_${this.props.messagesStore!.webpartId}`)) {
        let newContentLabelTextElement = document.getElementById(`IA_NewContentWrapper_${this.props.messagesStore!.webpartId}`);
        newContentLabelWidth = newContentLabelTextElement.clientWidth + 50;
      } else {
        setTimeout(() => {
          this.forceUpdate();
        }, 0);
      }
    }
    return (
      <>
        <div style={{ display: this.props.messagesStore?.isNavigator && (!this.props.messagesStore?.messageToOpen && !this.props.messagesStore?.messageToEdit) ? "none" : "flex" }}>
          <div
            id={this.props.messagesStore!.id}
            className={styles.IA_messagesContent}
            style={{
              height: this.props.messagesStore!.component === "Event" || this.props.messagesStore!.isMobileApp ? "auto" : "100%",
              paddingLeft: this.props.messagesStore!.isAdminPage ? 21 : 1,
              paddingRight: this.props.messagesStore!.isAdminPage ? 21 : 1,
              boxSizing: "border-box",
              paddingBottom: this.props.messagesStore?.isNavigator || this.props.messagesStore?.isHidden ? 0 : 10,
              position: "relative",
            }}
          >
            {this.props.messagesStore!.width && this.props.messagesStore!.component === "Messages" && this.props.messagesStore!.userRole !== "Reader" != undefined &&
              <SortPinnedPanel
                getMessages={() => this.getMessages()}
              />
            }
            {this.props.messagesStore!.width != undefined &&
              <ViewPanel
                id={this.props.messagesStore!.messageToOpen && this.props.messagesStore!.messageToOpen.id && openInNewViewer ? this.props.messagesStore!.messageToOpen.id : undefined}
                dataAutomationIdPrefix="messages-view-panel"
                getNextPage={() => this.getNextPage()}
                openingFromLink={!!this.props.hasOpenedContentFromLinkCallback}
                onClose={() => {
                  if (this.props.messagesStore!.cacheTimestamp && new DateTimeService().getDatePlusTwelveHours(new Date(this.props.messagesStore!.cacheTimestamp)) < new Date()) {
                    this.props.messagesStore!.pageNumber = 1;
                    this.props.messagesStore!.messages = [];
                    this.props.messagesStore!.loading = true;
                    this.getMessages();
                  }
                }}
              />
            }

            {this.props.messagesStore!.width != undefined &&
              <OldViewPanel
                id={this.props.messagesStore!.messageToOpen && this.props.messagesStore!.messageToOpen.id && !openInNewViewer ? this.props.messagesStore!.messageToOpen.id : undefined}
                dataAutomationIdPrefix="messages-view-panel"
                getNextPage={() => this.getNextPage()}
              />
            }
            {this.props.messagesStore!.width != undefined &&
              <EditPanel
                dataAutomationIdPrefix="messages-edit-panel"
                reloadMessages={() => {
                  this.props.messagesStore!.pageNumber = 1;
                  this.getMessages();
                }}
                saveTemplateCallback={(jsonTemplate) => {
                  this.props.saveTemplateCallback(jsonTemplate);
                  this.props.messagesStore!.messageToEdit = undefined;
                }}
              />
            }
            {this.props.messagesStore!.isAdminPage &&
              <DigestPanel
                dataAutomationIdPrefix="digest-panel"
              />
            }
            <div id={`notificationBarId_${this.notificationBarId}`}>
              {this.props.messagesStore!.notification != undefined &&
                <IAMessageBar
                  type={this.props.messagesStore!.notification.type}
                  onDismiss={() => this.props.messagesStore!.hideNotification()}
                  icon={this.props.messagesStore!.notification.icon}
                  content={this.props.messagesStore!.notification.content}
                  showIcon={this.props.messagesStore!.notification.icon != undefined}
                  showCloseButton={true}
                  style={{
                    position: "fixed",
                    top: this.props.messagesStore!.notification.position,
                    zIndex: 2000000,
                    left: "calc(50vw - 150px)",
                    width: 300,
                    transition: "top 500ms",
                    borderRadius: 5
                  }}
                />
              }
            </div>
            <div id={`convertPopupId_${this.convertPopupId}`}>
              {this.props.messagesStore!.showConvertToComposerPopup &&
                <IAPopup
                  headline={this.localizationService.strings.Messages_ConvertToComposer_Headline}
                  content={
                    <div>
                      <div
                        style={{
                          float: "left"
                        }}
                      >
                        {this.props.messagesStore!.component === "Event" ? this.localizationService.strings.Events_ConvertToComposer_Message : this.localizationService.strings.Messages_ConvertToComposer_Message}
                      </div>
                      <div
                        style={{
                          clear: "both",
                          float: "left"
                        }}
                      >
                        <IAButton
                          label={this.localizationService.strings.Messages_ConvertToComposer_ConvertButton}
                          buttonColor={this.props.messagesStore!.highlightColor}
                          showSpinner={false}
                          borderRadius={5}
                          onClick={() => {
                            this.props.messagesStore!.editMessage(this.props.messagesStore!.showConvertToComposerPopup);
                            this.props.messagesStore!.showConvertToComposerPopup = undefined;
                          }}
                          style={{
                            marginTop: 20,
                            float: "left"
                          }}
                        />
                      </div>
                    </div>
                  }
                  close={() => {
                    this.props.messagesStore!.showConvertToComposerPopup = undefined;
                  }}
                />
              }
            </div>
            <div id={`deletePopupId_${this.deletePopupId}`}>
              {this.props.messagesStore!.messageToDelete != undefined &&
                <IAPopup
                  headline={this.localizationService.strings.Messages_DeletePopup_Headline}
                  content={
                    <div>
                      <div
                        style={{
                          float: "left"
                        }}
                      >
                        {this.localizationService.strings.Messages_DeletePopup_Message}
                      </div>
                      <div
                        style={{
                          clear: "both",
                          float: "left"
                        }}
                      >
                        <IAButton
                          label={this.localizationService.strings.Messages_DeletePopup_Yes}
                          buttonColor={this.props.messagesStore!.highlightColor}
                          borderRadius={5}
                          showSpinner={false}
                          onClick={() => {
                            LoggingService.trackEvent(this.constructorName, {
                              ActionType: "DeleteMessage",
                              Component: "Messages",
                              Id: this.props.messagesStore!.messageToDelete.id,
                              Title: this.props.messagesStore!.messageToDelete.title,
                              Tenant: this.props.messagesStore!.tenant,
                              Instance: this.props.messagesStore!.instance.instanceName,
                              UserId: this.props.messagesStore!.token,
                              Environment: this.props.messagesStore!.environment
                            });
                            if (this.props.messagesStore!.component === "Event") {
                              EventService.deleteEvent(this.props.messagesStore!.messageToDelete.id.toString(), this.props.messagesStore!.environment != undefined ? this.props.messagesStore!.environment : "Production", this.props.messagesStore!.token, this.props.messagesStore!.tenant, this.props.messagesStore!.instance.instanceName).then(() => {
                                this.props.messagesStore!.messageToDelete = undefined;
                                this.props.messagesStore!.messageToEdit = undefined;
                                this.props.messagesStore!.pageNumber = 1;
                                document.body.style.overflowY = "auto";
                                document.body.style.height = "inherit";
                                this.getMessages();
                              });
                            } else {
                              MessageService.deleteMessage(this.props.messagesStore!.messageToDelete.id.toString(), this.props.messagesStore!.environment != undefined ? this.props.messagesStore!.environment : "Production", this.props.messagesStore!.token, this.props.messagesStore!.tenant, this.props.messagesStore!.instance.instanceName).then(() => {
                                this.props.messagesStore!.messageToDelete = undefined;
                                this.props.messagesStore!.messageToEdit = undefined;
                                this.props.messagesStore!.pageNumber = 1;
                                document.body.style.overflowY = "auto";
                                document.body.style.height = "inherit";
                                this.getMessages();
                              });
                            }
                          }}
                          style={{
                            marginTop: 20,
                            float: "left"
                          }}
                        />
                        <IAButton
                          label={this.localizationService.strings.Messages_DeletePopup_No}
                          buttonColor={"#999999"}
                          showSpinner={false}
                          borderRadius={5}
                          onClick={() => {
                            this.props.messagesStore!.messageToDelete = undefined;
                          }}
                          style={{
                            marginTop: 20,
                            marginLeft: 10,
                            float: "left"
                          }}
                        />
                      </div>
                    </div>
                  }
                  close={() => {
                    this.props.messagesStore!.messageToDelete = undefined;
                  }}
                />
              }
            </div>
            <div id={`cancelPopupId_${this.cancelPopupId}`}>
              {this.props.messagesStore!.showCancelEditPopup &&
                <IAPopup
                  headline={this.localizationService.strings.Messages_CancelPopup_Headline}
                  content={
                    <div>
                      <IAButton
                        label={this.localizationService.strings.Messages_CancelPopup_Yes}
                        buttonColor={this.props.messagesStore!.highlightColor}
                        showSpinner={false}
                        borderRadius={5}
                        onClick={() => {
                          if (this.props.messagesStore!.messageToEdit.iaConfiguration) {
                            this.props.messagesStore!.messageToEdit.iaConfiguration.cropSettings = this.props.messagesStore!.messageToEditOriginal.iaConfiguration?.cropSettings;
                          }
                          if (this.props.messagesStore!.showSortPinnedPanel) {
                            this.props.messagesStore!.refreshSortPinnedPanel = true;
                          }
                          this.props.messagesStore!.messageToEdit = undefined;
                          this.props.messagesStore!.messageToEditOriginal = undefined;
                          this.props.messagesStore!.creatingNewMessage = false;
                          this.props.messagesStore!.showCancelEditPopup = false;
                          // If the panel closes like this, it does not reset the body styles
                          document.body.style.overflowY = "auto";
                          document.body.style.height = "inherit";
                        }}
                        style={{
                          marginTop: 20,
                          float: "left"
                        }}
                      />
                      <IAButton
                        label={this.localizationService.strings.Messages_CancelPopup_No}
                        buttonColor={"#999999"}
                        showSpinner={false}
                        borderRadius={5}
                        onClick={() => {
                          this.props.messagesStore!.showCancelEditPopup = false;
                        }}
                        style={{
                          marginTop: 20,
                          marginLeft: 10,
                          float: "left"
                        }}
                      />
                    </div>
                  }
                  close={() => {
                    this.props.messagesStore!.showCancelEditPopup = false;
                  }}
                />
              }
            </div>

            {this.props.messagesStore!.isAdminPage && this.props.messagesStore!.userRole && (this.props.messagesStore!.userRole === "Administrator" || this.props.messagesStore!.userRole === "Editor") && this.state.selectedTab === "Messages" && this.props.messagesStore!.component === "Messages" &&
              <IAButton label={this.localizationService.strings.Messages_GenerateNewsDigest}
                icon="News"
                iconColor={"#000"}
                height={22}
                fontSize={14}
                borderRadius={5}
                style={{
                  backgroundColor: "#f0f0f0",
                  padding: "5px 14px",
                  fontWeight: "600",
                  height: "auto",
                  position: "relative",
                  float: "right"
                }}
                onClick={() => this.props.messagesStore!.digestPaneIsOpen = true} />
            }
            {this.props.messagesStore!.isAdminPage && this.props.messagesStore!.userRole && this.props.messagesStore!.userRole === "Administrator" &&
              <div style={{
                float: "left",
                width: "fit-content"
              }}>
                <IAPivot
                  highlightColor="#23879b"
                  items={[
                    {
                      key: "Messages",
                      text: this.props.messagesStore!.component === "Messages" ? this.localizationService.strings.Messages_PivotMessages : this.localizationService.strings.EventPivotEvents
                    },
                    ...(this.props.messagesStore!.component === "Event" ? [{
                      key: "Recurring",
                      text: "Recurring"
                    }] : []),
                    {
                      key: "Categories",
                      text: this.localizationService.strings.Messages_PivotCategories
                    }
                  ]}
                  selectedKey={this.state.selectedTab}
                  select={(selectedTab: string) => this.setState({ selectedTab: selectedTab as "Messages" | "Recurring" | "Categories" })}
                />
              </div>
            }
            {this.state.selectedTab === "Messages" &&
              <div className={hideComponent ? styles.IA_hideMessages : ''} >
                <Header
                  getMessages={() => this.getMessages()}
                  close={this.props.close ? () => this.props.close() : undefined}
                  onTitleSettingsChange={this.props.onTitleSettingsChange}
                />
                {this.props.messagesStore!.newContentAvailable &&
                  <div
                    className={styles.IA_newContentWrapper}
                    style={{
                      width: newContentLabelWidth,
                      marginLeft: `calc(50% - ${newContentLabelWidth / 2}px)`
                    }}
                  >
                    <div
                      className={styles.IA_newContent}
                      style={{
                        backgroundColor: this.props.messagesStore!.highlightColor,
                        color: Helper.isLightColor(this.props.messagesStore!.highlightColor) ? "#333333" : "#ffffff"
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        this.props.messagesStore!.pageNumber = 1;
                        this.props.messagesStore!.newContentAvailable = false;
                        this.props.messagesStore!.loading = true;
                        this.getMessages();
                      }}
                    >
                      <IAIcon
                        title={"Refresh"}
                        size={18}
                        color={Helper.isLightColor(this.props.messagesStore!.highlightColor) ? "#333333" : "#ffffff"}
                        style={{
                          float: "left",
                          marginTop: 5,
                          marginLeft: 12,
                          marginRight: 5
                        }}
                      />
                      <div style={{ float: "left" }} id={`IA_NewContentWrapper_${this.props.messagesStore!.webpartId}`}>{this.localizationService.strings.Messages_NewContent}</div>
                    </div>
                  </div>
                }
                {showNoContentMessage &&
                  <p style={{ fontFamily: this.props.messagesStore!.fontSlots?.body?.fontFamily, padding: '1rem', margin: '0 auto' }}>{!this.props.messagesStore!.textIfNoContent ? this.localizationService.strings.Messages_NoContentToShow : this.props.messagesStore!.textIfNoContent}</p>
                }
                {this.props.messagesStore!.display === "ReelsView" &&
                  <ReelsView
                    messages={this.props.messagesStore!.messages}
                    environment={this.props.messagesStore!.environment as "Production" | "Development"}
                    tenant={this.props.messagesStore!.tenant}
                    token={this.props.messagesStore!.token}
                    highlightColor={this.props.messagesStore!.highlightColor}
                    rollupHighlightColor={this.props.messagesStore!.rollupHighlightColor ?? this.props.messagesStore!.highlightColor}
                    instanceName={this.props.messagesStore!.instance?.instanceName}
                    user={this.props.messagesStore!.user}
                    showAuthor={this.props.messagesStore?.showAuthor}
                    source={this.props.messagesStore!.source}
                    showEmojiPickerExpandButton={this.props.messagesStore!.instance?.settings?.showEmojiPickerExpandButton}
                    reactionOptions={this.props.messagesStore!.instance?.settings?.reactionOptions ? this.props.messagesStore!.instance?.settings?.reactionOptions : []}
                    likesUpdated={(likes: ILike[]) => this.props.messagesStore!.likesUpdated(likes)}
                    isMobile={this.props.messagesStore!.isMobile ?? false}
                    type={this.props.messagesStore!.component === "Messages" ? "Message" : "Event"}
                    likesEnabled={this.props.messagesStore!.likesEnabled}
                    showUniqueViews={this.props.messagesStore!.showUniqueViews}
                    fontSlots={this.props.messagesStore!.fontSlots}
                    showTitle={this.props.messagesStore!.showTitleInReelsView != undefined ? this.props.messagesStore!.showTitleInReelsView : "show"}
                    borderRadius={this.props.messagesStore!.borderRadius}
                    backgroundColor={this.props.messagesStore!.backgroundColor}
                    textColor={this.props.messagesStore!.textColor}
                    showDropShadow={this.props.messagesStore!.showDropShadow}
                    showReelsInOneLine={this.props.messagesStore!.showReelsInOneLine ?? true}
                    pageNumber={this.props.messagesStore!.pageNumber}
                    numMessages={this.props.messagesStore!.numMessages}
                    showCategories={this.props.messagesStore!.showCategories}
                    borderColor={this.props.messagesStore!.borderColor}
                    enableReadUnreadMessage={this.props.messagesStore.instance?.settings?.enableReadUnreadMessage}
                    highlightUnread={this.props.messagesStore.instance?.settings?.highlightUnread ? this.props.messagesStore.instance?.settings?.highlightUnread : "red"}
                    onEditMessage={this.props.messagesStore!.isAdminPage ? (message) => {
                      this.props.messagesStore!.editMessage(message);
                    } : undefined}
                  />
                }
                {this.props.messagesStore!.display === "Card view" &&
                  <CardView />
                }
                {this.props.messagesStore!.display === "List view" && this.props.messagesStore!.component === "Messages" &&
                  <ListView />
                }
                {this.props.messagesStore!.display === "CompactListView" && this.props.messagesStore!.component === "Messages" &&
                  <CompactListView />
                }
                {this.props.messagesStore!.display === "List view" && this.props.messagesStore!.component === "Event" &&
                  <EventListView />
                }
                {this.props.messagesStore!.width > 750 && (this.props.messagesStore!.display === "CarouselView" || this.props.messagesStore!.display === "SplitCarouselView") &&
                  <CarrouselView />
                }
                {(this.props.messagesStore!.display === "RotatorView" || (this.props.messagesStore!.display === "CarouselView" || this.props.messagesStore!.display === "SplitCarouselView" || this.props.messagesStore!.display === "TilesView") && this.props.messagesStore!.width <= 750) &&
                  <Rotator />
                }
                {this.props.messagesStore!.width > 750 && (this.props.messagesStore!.display === "TilesView") &&
                  <TilesView />
                }
                {this.props.messagesStore!.display === "fullView" &&
                  <FullView
                    getNextPage={() => {
                      return this.getNextPage();
                    }}
                    getPreviousPage={() => {
                      this.getPreviousPage();
                    }}
                  />
                }
                {this.props.messagesStore!.display === "Calendar" && this.props.messagesStore!.component === "Event" && !showNoContentMessage &&
                  <CalendarView
                    getMessages={() => this.getMessages()}
                  />
                }
                {/* {!this.props.messagesStore!.loading && (this.props.messagesStore!.messages == undefined || (this.props.messagesStore!.messages && this.props.messagesStore!.messages.length === 0)) &&
                <div className={styles.IA_noMessages}>{this.localizationService.strings.Messages_ViewPanel_NoMessages}</div>
              } */}
                {this.props.messagesStore!.showPaging && this.props.messagesStore!.width != undefined && this.props.messagesStore!.display != "fullView" && (this.props.messagesStore!.display != "ReelsView" || (this.props.messagesStore!.display === "ReelsView" && (this.props.messagesStore!.showReelsInOneLine === false || this.props.messagesStore!.isMobile))) && this.props.messagesStore!.display != "RotatorView" && this.props.messagesStore!.display != "SplitCarouselView" && !((this.props.messagesStore!.display === "TilesView" || this.props.messagesStore!.display === "CarouselView") && this.props.messagesStore!.width <= 750) && this.props.messagesStore!.messages?.length > 0 &&
                  <Paging
                    isLoading={this.props.messagesStore!.loading}
                    pageNumber={this.props.messagesStore!.pageNumber}
                    hasNextPage={this.hasNextPage()}
                    showPaging={this.showPaging()}
                    hasDarkBackground={this.props.messagesStore!.hasDarkBackground}
                    highlightColor={this.props.messagesStore!.highlightColor}
                    isMobile={this.props.messagesStore!.isMobile}
                    getNextPage={() => {
                      if (this.props.messagesStore!.pageNumber * this.props.messagesStore!.numMessages >= this.props.messagesStore!.messages.length) {
                        this.getNextPage();
                      }
                      this.props.messagesStore!.pageNumber++;
                    }}
                    getPreviousPage={() => {
                      this.getPreviousPage();
                    }}
                  />
                }
                {this.props.messagesStore!.isMobile && this.props.messagesStore!.showPaging && this.props.messagesStore!.width != undefined && (this.props.messagesStore!.loading || this.props.messagesStore!.loadingCommentsAndLikes || this.props.messagesStore!.loadingCommentsAndLikes) && this.props.messagesStore!.display != "fullView" && this.props.messagesStore!.display != "RotatorView" && this.props.messagesStore!.display != "SplitCarouselView" && this.props.messagesStore!.display != "CarouselView" &&
                  <div className={styles.IA_loading}>
                    <div className={styles.loader} />
                  </div>
                }
              </div>
            }
            {this.state.selectedTab === "Categories" &&
              <IACategories
                environment={this.props.messagesStore!.environment}
                instance={this.props.messagesStore!.instance}
                tenant={this.props.messagesStore!.tenant}
                highlightColor={this.props.messagesStore!.highlightColor}
                token={this.props.messagesStore!.token}
                site={this.props.messagesStore!.component === "Messages" ? "IntraActive-Messages" : "IntraActive-Events"}
                component={this.props.messagesStore!.component}
                disableUnsync={this.props.messagesStore!.instance?.settings?.categoriesRequired}
                onChange={(category: ICategoryAdmin) => {
                  console.log(category);
                  this.props.messagesStore!.getCategories(true);
                }}
              />
            }
            {this.state.selectedTab === "Recurring" &&
              <>
                <IARecurringEvents
                  environment={this.props.messagesStore!.environment}
                  instanceName={this.props.messagesStore!.instance.instanceName}
                  tenant={this.props.messagesStore!.tenant}
                  onOpenEvent={(event) => {
                    this.props.messagesStore!.openMessage(event);

                  }}
                />
              </>
            }
          </div>
        </div>
        {this.props.messagesStore!.reelsMessageToOpen && this.props.messagesStore!.openedFromLink && (
          <ReelsPopup
            message={this.props.messagesStore!.reelsMessageToOpen}
            environment={this.props.messagesStore!.environment as "Production" | "Development"}
            tenant={this.props.messagesStore!.tenant}
            token={this.props.messagesStore!.token}
            highlightColor={this.props.messagesStore!.highlightColor}
            rollupHighlightColor={this.props.messagesStore!.rollupHighlightColor ?? this.props.messagesStore!.highlightColor}
            user={this.props.messagesStore!.user}
            type={this.props.messagesStore!.component === "Messages" ? "Message" : "Event"}
            instanceName={this.props.messagesStore!.instance?.instanceName}
            source={this.props.messagesStore!.source}
            isMobile={this.props.messagesStore!.isMobile}
            fontSlots={this.props.messagesStore!.fontSlots}
            likesEnabled={this.props.messagesStore!.likesEnabled}
            showUniqueViews={this.props.messagesStore!.showUniqueViews}
            showCategories={this.props.messagesStore!.showCategories}
            showEmojiPickerExpandButton={this.props.messagesStore!.instance?.settings?.showEmojiPickerExpandButton}
            reactionOptions={this.props.messagesStore!.instance?.settings?.reactionOptions ?? []}
            likesUpdated={(likes) => this.props.messagesStore!.likesUpdated(likes)}
            onClose={() => this.props.messagesStore!.reelsMessageToOpen = undefined}
          />
        )}
      </>
    );
  }



  public componentWillUnmount(): void {
    window.removeEventListener('resize', () => this.resize());
    this._isMounted = false;
  }
}